import { axios } from '@/api/axios.ts';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { TCreateTask, TTask } from './tasks.types.ts';

type TPaginatedTasks = {
  data: TTask[];
  meta: { total: number; page: number; limit: number; totalPages: number };
};

export const tasksApi = {
  getTasks: async () => {
    // GET /tasks is paginated and returns { data, meta }, not a bare array.
    // The admin screens need the whole catalogue at once (task counts per
    // attribute), so request a limit well above the current row count instead
    // of paging through it.
    const response = await axios.get<TPaginatedTasks>('tasks', {
      params: { limit: 10000 },
    });

    return response.data.data;
  },
  getTaskById: async (id: number) => {
    const response = await axios.get<TTask>(`tasks/${id}`);

    return response.data;
  },
  createTask: async (task: TCreateTask) => {
    const response = await axios.post<TTask>('tasks', {
      ...task,
      priority: task.priority.toUpperCase(),
    });

    return response.data;
  },
  updateTask: async (payload: { id: number; task: Partial<TTask> }) => {
    const { id, task } = payload;
    const response = await axios.patch<TTask>(`tasks/${id}`, {
      ...task,
      priority: task.priority ? task.priority.toUpperCase() : undefined,
    });
    return response.data;
  },
  deleteTask: async (id: number) => {
    const response = await axios.delete<{ message: string }>(`tasks/${id}`);

    return response.data;
  },
};

export const tasksQueries = createQueryKeys('tasks', {
  getTasks: () => ({
    queryKey: ['list'],
    queryFn: () => tasksApi.getTasks(),
  }),
  getTaskById: (id: number) => ({
    queryKey: ['task', id],
    queryFn: () => tasksApi.getTaskById(id),
  }),
});
